'use client'
import { useState, useEffect, useCallback, useRef } from 'react'
import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import type { SectionConfig } from '@/types/site'
import type { PricingContent, PricingPlan } from '@/types/sections'
import { Check, X } from 'lucide-react'
import { getTitleSizeClass, getTextAlignClass } from '../_utils'
import { elementProps } from '@/lib/elementHelpers'
import GrandTitle from '@/components/GrandTitle'
import { EditablePlaceholder } from '../_EditablePlaceholder'
import ReservationModal from '@/components/ReservationModal'
import { getReservationForm, type ReservationFormConfig } from '@/lib/reservationForm'

// ─── la billetterie Fullscreen Checkout ──────────────────────────
function BilletterieModal({ embedUrl, accentColor, variant, footerText, onClose }: { embedUrl: string; accentColor: string; variant: 'dark' | 'light'; footerText?: string; onClose: () => void }) {
  const isDark = variant === 'dark'
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Slide-in animation
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', handleEsc); document.body.style.overflow = '' }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[99999] transition-transform duration-500 ease-out"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        backgroundColor: isDark ? '#060606' : '#f9fafb',
      }}
    >
      {/* Top bar */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-10 py-4"
        style={{
          backgroundColor: isDark ? 'rgba(6,6,6,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb'}`,
        }}
      >
        {/* Back button */}
        <button
          onClick={onClose}
          className={cn(
            'flex items-center gap-2 text-sm font-medium transition-colors',
            isDark ? 'text-brand-ink/60 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'
          )}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
          </svg>
          Retour
        </button>

        {/* Center title */}
        <div className="absolute left-1/2 -translate-x-1/2 text-center">
          <p className="text-[10px] font-medium tracking-[0.25em] uppercase" style={{ color: accentColor }}>Réservation</p>
          {/* TODO P2 : rendre ces libellés pilotés par l'événement/config au lieu d'être en dur. */}
          <h3 className={cn('text-sm font-semibold', isDark ? 'text-white' : 'text-zinc-900')}>Grand Gala des Étoiles — Bal de l&apos;Infini · 3 octobre 2026</h3>
        </div>

        {/* Accent dot */}
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accentColor, boxShadow: `0 0 8px ${accentColor}60` }} />
      </div>

      {/* Gold decorative line */}
      {isDark && (
        <div className="h-px" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}40, transparent)` }} />
      )}

      {/* Content area */}
      <div className="h-[calc(100vh-60px)] overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
          {/* Elegant header */}
          <div className="text-center mb-8">
            <h2 className={cn('text-2xl md:text-3xl font-semibold tracking-tight', isDark ? 'text-white' : 'text-zinc-900')}>
              Choisissez votre formule
            </h2>
            <p className={cn('text-sm mt-2', isDark ? 'text-brand-ink/40' : 'text-zinc-500')}>
              Paiement sécurisé via la billetterie — Places limitées
            </p>
            {isDark && (
              <div className="w-16 h-px mx-auto mt-4" style={{ background: accentColor }} />
            )}
          </div>

          {/* la billetterie iframe */}
          <div
            className="rounded-xl overflow-hidden"
            style={{
              border: isDark ? `1px solid ${accentColor}20` : '1px solid #e5e7eb',
              boxShadow: isDark ? `0 0 40px ${accentColor}08` : '0 4px 24px rgba(0,0,0,0.06)',
            }}
          >
            <iframe
              src={embedUrl}
              className="w-full"
              style={{ minHeight: '700px', background: 'white', border: 'none', display: 'block' }}
              allow="payment"
            />
          </div>

          {/* Le lieu, quand il est renseigné dans l'éditeur (section Formules,
              champ « Pied de la billetterie »). Il était écrit en dur ici :
              changer de salle demandait de modifier le code et de redéployer. */}
          {footerText ? (
            <div className={cn('text-center mt-6 text-xs', isDark ? 'text-brand-ink/25' : 'text-zinc-400')}>
              {footerText}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

interface PricingSectionProps {
  config: SectionConfig
  isEditing?: boolean
}

// ─── Helpers ───────────────────────────────────────────────

/** Return period string only if it doesn't contain subscription-style text like "/Monthly". */
function displayPeriod(period?: string): string | null {
  if (!period) return null
  if (/month/i.test(period)) return null
  return period
}

/** Collect every unique feature text across all plans (preserving order). */
function allFeatureTexts(plans: PricingPlan[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const plan of plans) {
    for (const f of plan.features) {
      if (!seen.has(f.text)) {
        seen.add(f.text)
        result.push(f.text)
      }
    }
  }
  return result
}

/** Whether a plan includes a given feature text. */
function planHasFeature(plan: PricingPlan, text: string): boolean {
  return plan.features.some(f => f.text === text && f.included)
}

// ─── Main component ────────────────────────────────────────

export function PricingSection({ config, isEditing }: PricingSectionProps) {
  const content = (config.content ?? {}) as Partial<PricingContent>
  /* Formule « sur demande » : le bouton ouvre un e-mail pré-rempli plutôt qu'un
     lien de réservation. Éditable par formule (case « Bouton Nous contacter »).
     Le prix, lui, se masque avec la case « Masquer le prix ». */
  const CONTACT_EMAIL = 'contact@rstars.fr'
  const hrefContact = (nomFormule: string) => {
    const sujet = `Demande d'information — ${nomFormule || 'une formule'}`
    const corps = `Bonjour,\n\nJe souhaite recevoir des informations concernant la formule « ${nomFormule || 'sélectionnée'} » du Gala des Étoiles.\n\nMerci.`
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`
  }
  const plans: PricingPlan[] = (content.plans ?? []).map((p) => (
    p.boutonContact
      ? { ...p, ctaHref: hrefContact(p.name), cta: p.cta || 'Nous contacter' }
      : p
  ))
  const variant = config.variant ?? 'startup-columns'
  const { accentColor, titleSize, textAlign, textColor } = config.style
  const isDark = config.style?.background === 'dark'
  const embedUrl = (content as any).embedUrl as string | undefined
  const footerText = (content as any).footerText as string | undefined
  const [modalOpen, setModalOpen] = useState(false)
  const closeModal = useCallback(() => setModalOpen(false), [])

  /* Mode « demande de réservation » (case CRM `content.modeReservation`) :
     masque tous les prix (même effet que `masquerPrix` par formule) et
     remplace les boutons par une modale-formulaire dynamique, alimentée par
     la config publiée au CRM. N'est branché ici que pour la variante
     glass-columns : c'est la seule utilisée par le Gala des Étoiles (les
     autres univers — startup/corporate/luxe/creative/ecommerce — ne sont
     pas utilisés par ce site et n'ont pas besoin de ce mode). */
  const modeReservation = Boolean(content.modeReservation)
  const nomsFormules = plans.map((p) => p.name)
  const [reservationOpen, setReservationOpen] = useState(false)
  const [reservationFormule, setReservationFormule] = useState('')
  // undefined = chargement en cours, null = CRM injoignable (repli), objet = chargé.
  const [reservationConfig, setReservationConfig] = useState<ReservationFormConfig | null | undefined>(undefined)
  const openReservation = useCallback((nomFormule: string) => {
    setReservationFormule(nomFormule)
    setReservationOpen(true)
    setReservationConfig(undefined)
    // La modale fait sa propre requête à l'ouverture — la config CRM la
    // plus fraîche possible, jamais de cache périmé entre deux éditions.
    getReservationForm().then(setReservationConfig)
  }, [])
  const closeReservation = useCallback(() => setReservationOpen(false), [])

  // Mobile carousel state (used by glass-columns variant only)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [currentMobileIdx, setCurrentMobileIdx] = useState(0)
  useEffect(() => {
    const el = carouselRef.current
    if (!el) return
    const onScroll = () => {
      const cardWidth = el.scrollWidth / Math.max(1, plans.length)
      const idx = Math.round(el.scrollLeft / cardWidth)
      setCurrentMobileIdx(Math.max(0, Math.min(plans.length - 1, idx)))
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [plans.length])
  const scrollToCard = useCallback((idx: number) => {
    const el = carouselRef.current
    if (!el) return
    const cardWidth = el.scrollWidth / Math.max(1, plans.length)
    el.scrollTo({ left: idx * cardWidth, behavior: 'smooth' })
  }, [plans.length])

  // Parse variant
  const [universe, layout] = variant.includes('-')
    ? [variant.split('-').slice(0, -1).join('-'), variant.split('-').pop()!]
    : [variant, 'columns']

  // ─────────────────────────────────────────────
  // STARTUP
  // ─────────────────────────────────────────────
  if (universe === 'startup') {
    const accent = accentColor ?? '#6366f1'

    if (layout === 'comparison') {
      const features = allFeatureTexts(plans)
      return (
        <section className="relative bg-zinc-50 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 py-20">
            {/* Header */}
            <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
              {content.eyebrow ? (
                <span
                  className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-brand-ink"
                  style={{ backgroundColor: accent }}
                >
                  {content.eyebrow}
                </span>
              ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
              {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
              {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
            </div>

            {/* Comparison table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-zinc-500 w-1/4">Fonctionnalités</th>
                    {plans.map((plan, planIdx) => (
                      <th key={plan.id} className="p-4 text-center">
                        <div className={cn(
                          'rounded-2xl p-5 transition-all',
                          plan.highlighted
                            ? 'bg-white shadow-xl ring-2 scale-105'
                            : 'bg-white shadow-sm'
                        )}
                          style={plan.highlighted ? { ringColor: accent, '--tw-ring-color': accent } as React.CSSProperties : undefined}
                        >
                          {plan.badge && (
                            <span
                              className="inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-full text-brand-ink mb-2"
                              style={{ backgroundColor: accent }}
                            >
                              {plan.badge}
                            </span>
                          )}
                          <div {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-bold text-lg text-zinc-900">{plan.name}</div>
                          <div className="flex items-end justify-center gap-1 mt-1">
                            <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-3xl font-bold text-zinc-900">{plan.price}</span>
                            <span className="text-sm text-zinc-400 mb-1">{displayPeriod(plan.period)}</span>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map((feat, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}>
                      <td className="p-4 text-sm text-zinc-700">{feat}</td>
                      {plans.map((plan, planIdx) => (
                        <td key={plan.id} className="p-4 text-center">
                          {planHasFeature(plan, feat)
                            ? <Check className="w-5 h-5 mx-auto" style={{ color: accent }} />
                            : <X className="w-5 h-5 mx-auto text-zinc-300" />
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="p-4" />
                    {plans.map((plan, planIdx) => (
                      <td key={plan.id} className="p-4 text-center">
                        <a
                          {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                          className={cn(
                            'inline-block px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors',
                            plan.highlighted
                              ? 'text-brand-ink hover:opacity-90'
                              : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                          )}
                          style={plan.highlighted ? { backgroundColor: accent } : undefined}
                        >
                          {plan.cta}
                        </a>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>
      )
    }

    // startup-columns (default)
    return (
      <section className="relative bg-zinc-50 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-6xl mx-auto px-6 py-20">
          {/* Header */}
          <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
            {content.eyebrow ? (
              <span
                className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-brand-ink"
                style={{ backgroundColor: accent }}
              >
                {content.eyebrow}
              </span>
            ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
            {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
            {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
          </div>

          {/* Cards */}
          <div className={cn(
            'grid gap-6 items-start',
            plans.length <= 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : plans.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'
          )}>
            {plans.map((plan, planIdx) => (
              <div
                key={plan.id}
                className={cn(
                  'rounded-2xl p-6 relative transition-all',
                  plan.highlighted
                    ? 'text-brand-ink scale-105 shadow-2xl ring-2'
                    : 'bg-white shadow-sm border border-zinc-100'
                )}
                style={plan.highlighted
                  ? { backgroundColor: accent, ringColor: accent, '--tw-ring-color': accent, boxShadow: `0 20px 50px -12px ${accent}40` } as React.CSSProperties
                  : undefined
                }
              >
                {plan.badge && (
                  <span className={cn(
                    'absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full',
                    plan.highlighted ? 'bg-amber-400 text-amber-900' : 'bg-zinc-800 text-white'
                  )}>
                    {plan.badge}
                  </span>
                )}
                <div className="mb-6">
                  <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className={cn('font-bold text-lg mb-1', plan.highlighted ? 'text-white' : 'text-zinc-900')}>{plan.name}</h3>
                  <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className={cn('text-xs mb-4', plan.highlighted ? 'text-brand-ink/70' : 'text-zinc-500')}>{plan.description}</p>
                  <div className="flex items-end gap-2">
                    <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className={cn('text-4xl font-bold', plan.highlighted ? 'text-white' : 'text-zinc-900')}>{plan.price}</span>
                    {(plan as any).originalPrice && (
                      <span className={cn('text-lg line-through mb-1', plan.highlighted ? 'text-brand-ink/40' : 'text-zinc-400')}>{(plan as any).originalPrice}</span>
                    )}
                    <span className={cn('text-sm mb-1', plan.highlighted ? 'text-brand-ink/70' : 'text-zinc-400')}>{displayPeriod(plan.period)}</span>
                  </div>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={cn('flex items-center gap-2 text-sm',
                      plan.highlighted ? 'text-white' : feature.included ? 'text-zinc-700' : 'text-zinc-400'
                    )}>
                      {feature.included
                        ? <Check className={cn('w-4 h-4 shrink-0', plan.highlighted ? 'text-brand-ink/80' : '')} style={!plan.highlighted ? { color: accent } : undefined} />
                        : <X className="w-4 h-4 shrink-0 opacity-40" />
                      }
                      <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                {embedUrl ? (
                  <button
                    {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                    onClick={() => setModalOpen(true)}
                    className={cn(
                      'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer',
                      plan.highlighted
                        ? 'bg-white hover:bg-white/90'
                        : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-200'
                    )}
                    style={plan.highlighted ? { color: accent } : undefined}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <a
                    {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                    href={plan.ctaHref}
                    className={cn(
                      'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-colors',
                      plan.highlighted
                        ? 'bg-white hover:bg-white/90'
                        : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-200'
                    )}
                    style={plan.highlighted ? { color: accent } : undefined}
                  >
                    {plan.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
          {/* la billetterie modal */}
          {modalOpen && embedUrl && (
            <BilletterieModal embedUrl={embedUrl} accentColor={accent} variant="light" footerText={footerText} onClose={closeModal} />
          )}
        </div>
      </section>
    )
  }

  // ─────────────────────────────────────────────
  // CORPORATE
  // ─────────────────────────────────────────────
  if (universe === 'corporate') {
    const accent = accentColor ?? '#3b82f6'

    if (layout === 'comparison') {
      const features = allFeatureTexts(plans)
      return (
        <section className="relative bg-slate-900 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          {/* Noise overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'none' }} />
          <div className="relative max-w-5xl mx-auto px-6 py-20">
            <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
              {content.eyebrow ? (
                <span className="inline-block text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded border border-blue-500/30 text-blue-400">
                  {content.eyebrow}
                </span>
              ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
              {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-brand-ink", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
              {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-slate-400 max-w-2xl mx-auto">{content.subtitle}</p>}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400 w-1/4">Fonctionnalités</th>
                    {plans.map((plan, planIdx) => (
                      <th key={plan.id} className="p-4 text-center">
                        <div className={cn(
                          'rounded-lg p-5',
                          plan.highlighted
                            ? 'bg-slate-800 border-2'
                            : 'bg-slate-800/50 border border-slate-700'
                        )}
                          style={plan.highlighted ? { borderColor: accent } : undefined}
                        >
                          {plan.highlighted && (
                            <div className="h-1 rounded-full mb-3 -mx-1" style={{ background: `linear-gradient(to right, ${accent}, #1e40af)` }} />
                          )}
                          {plan.badge && (
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded text-brand-ink mb-2" style={{ backgroundColor: accent }}>
                              {plan.badge}
                            </span>
                          )}
                          <div {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-bold text-lg text-brand-ink">{plan.name}</div>
                          <div className="flex items-end justify-center gap-1 mt-1">
                            <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-3xl font-bold text-brand-ink">{plan.price}</span>
                            <span className="text-sm text-slate-400 mb-1">{displayPeriod(plan.period)}</span>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map((feat, i) => (
                    <tr key={i} className={cn('border-b border-slate-800', i % 2 === 0 ? 'bg-slate-800/20' : '')}>
                      <td className="p-4 text-sm text-slate-300">{feat}</td>
                      {plans.map((plan, planIdx) => (
                        <td key={plan.id} className="p-4 text-center">
                          {planHasFeature(plan, feat)
                            ? <Check className="w-5 h-5 mx-auto" style={{ color: accent }} />
                            : <X className="w-5 h-5 mx-auto text-slate-600" />
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="p-4" />
                    {plans.map((plan, planIdx) => (
                      <td key={plan.id} className="p-4 text-center">
                        <a
                          {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                          className={cn(
                            'inline-block px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors',
                            plan.highlighted
                              ? 'text-brand-ink hover:opacity-90'
                              : 'bg-slate-700 text-brand-ink hover:bg-slate-600'
                          )}
                          style={plan.highlighted ? { backgroundColor: accent } : undefined}
                        >
                          {plan.cta}
                        </a>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>
      )
    }

    // corporate-columns
    return (
      <section className="relative bg-slate-900 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        {/* Noise overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'none' }} />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
            {content.eyebrow ? (
              <span className="inline-block text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded border border-blue-500/30 text-blue-400">
                {content.eyebrow}
              </span>
            ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
            {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-brand-ink", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
            {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-slate-400 max-w-2xl mx-auto">{content.subtitle}</p>}
          </div>

          <div className={cn(
            'grid gap-6 items-start',
            plans.length <= 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : plans.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'
          )}>
            {plans.map((plan, planIdx) => (
              <div
                key={plan.id}
                className={cn(
                  'rounded-lg p-6 relative',
                  plan.highlighted
                    ? 'bg-slate-800 border-2'
                    : 'bg-slate-800/60 border border-slate-700'
                )}
                style={plan.highlighted ? { borderColor: accent } : undefined}
              >
                {/* Gradient top bar for highlighted */}
                {plan.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-1 rounded-t-lg" style={{ background: `linear-gradient(to right, ${accent}, #1e40af)` }} />
                )}
                {plan.badge && (
                  <span className={cn(
                    'absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded',
                    plan.highlighted ? 'text-white' : 'bg-slate-600 text-white'
                  )}
                    style={plan.highlighted ? { backgroundColor: accent } : undefined}
                  >
                    {plan.badge}
                  </span>
                )}
                <div className="mb-6">
                  <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-bold text-lg mb-1 text-brand-ink">{plan.name}</h3>
                  <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className="text-xs mb-4 text-slate-400">{plan.description}</p>
                  <div className="flex items-end gap-1">
                    <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-4xl font-bold text-brand-ink">{plan.price}</span>
                    <span className="text-sm mb-1 text-slate-400">{displayPeriod(plan.period)}</span>
                  </div>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={cn('flex items-center gap-2 text-sm',
                      feature.included ? 'text-slate-200' : 'text-slate-500'
                    )}>
                      {feature.included
                        ? <Check className="w-4 h-4 shrink-0" style={{ color: accent }} />
                        : <X className="w-4 h-4 shrink-0 opacity-40" />
                      }
                      <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                  className={cn(
                    'block w-full text-center py-2.5 rounded-lg text-sm font-semibold transition-colors',
                    plan.highlighted
                      ? 'text-brand-ink hover:opacity-90'
                      : 'bg-slate-700 text-brand-ink hover:bg-slate-600 border border-slate-600'
                  )}
                  style={plan.highlighted ? { backgroundColor: accent } : undefined}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ─────────────────────────────────────────────
  // LUXE
  // ─────────────────────────────────────────────
  if (universe === 'luxe') {
    const gold = accentColor ?? '#CBB68B'

    if (layout === 'comparison') {
      const features = allFeatureTexts(plans)
      return (
        <section className="relative bg-white overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 py-24">
            <div className={cn("text-center mb-16 space-y-4", textAlign && getTextAlignClass(textAlign))}>
              {content.eyebrow ? (
                <span className="inline-block text-[11px] font-normal tracking-[0.25em] uppercase" style={{ color: gold }}>
                  {content.eyebrow}
                </span>
              ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
              {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-normal tracking-wide text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
              {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base font-normal text-zinc-400 max-w-2xl mx-auto tracking-wide">{content.subtitle}</p>}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-4 text-[11px] font-normal tracking-[0.2em] uppercase text-zinc-400 w-1/4">Fonctionnalités</th>
                    {plans.map((plan, planIdx) => (
                      <th key={plan.id} className="p-4 text-center">
                        <div className={cn(
                          'p-5 border',
                          plan.highlighted ? 'border-2' : 'border-zinc-200'
                        )}
                          style={plan.highlighted ? { borderColor: gold } : undefined}
                        >
                          {plan.badge && (
                            <span className="inline-block text-[10px] font-normal tracking-[0.2em] uppercase mb-2" style={{ color: gold }}>
                              {plan.badge || 'Recommandé'}
                            </span>
                          )}
                          <div {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-normal text-lg tracking-wide text-zinc-900">{plan.name}</div>
                          <div className="flex items-end justify-center gap-1 mt-2">
                            <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-3xl font-normal text-zinc-900">{plan.price}</span>
                            <span className="text-sm font-normal text-zinc-400 mb-1">{displayPeriod(plan.period)}</span>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map((feat, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="p-4 text-sm font-normal text-zinc-600 tracking-wide">{feat}</td>
                      {plans.map((plan, planIdx) => (
                        <td key={plan.id} className="p-4 text-center">
                          {planHasFeature(plan, feat)
                            ? <Check className="w-4 h-4 mx-auto" style={{ color: gold }} />
                            : <X className="w-4 h-4 mx-auto text-zinc-200" />
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="p-4" />
                    {plans.map((plan, planIdx) => (
                      <td key={plan.id} className="p-4 text-center">
                        <a
                          {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                          className={cn(
                            'inline-block px-8 py-2.5 text-sm font-normal tracking-wider uppercase transition-colors border',
                            plan.highlighted
                              ? 'text-brand-ink hover:opacity-90'
                              : 'text-zinc-700 border-zinc-300 hover:border-zinc-400'
                          )}
                          style={plan.highlighted ? { backgroundColor: gold, borderColor: gold } : undefined}
                        >
                          {plan.cta}
                        </a>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>
      )
    }

    // luxe-columns
    return (
      <section className="relative bg-white overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className={cn("text-center mb-16 space-y-4", textAlign && getTextAlignClass(textAlign))}>
            {content.eyebrow ? (
              <span className="inline-block text-[11px] font-normal tracking-[0.25em] uppercase" style={{ color: gold }}>
                {content.eyebrow}
              </span>
            ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
            {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-normal tracking-wide text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
            {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base font-normal text-zinc-400 max-w-2xl mx-auto tracking-wide">{content.subtitle}</p>}
          </div>

          <div className={cn(
            'grid gap-8 items-start',
            plans.length <= 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : plans.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'
          )}>
            {plans.map((plan, planIdx) => (
              <div
                key={plan.id}
                className={cn(
                  'p-8 relative border',
                  plan.highlighted ? 'border-2' : 'border-zinc-200'
                )}
                style={plan.highlighted ? { borderColor: gold } : undefined}
              >
                {plan.badge && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-normal tracking-[0.2em] uppercase px-4 py-1 bg-white"
                    style={{ color: gold }}
                  >
                    {plan.badge}
                  </span>
                )}
                <div className={cn("mb-8 text-center", textAlign && getTextAlignClass(textAlign))}>
                  <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-normal text-lg tracking-wide text-zinc-900 mb-2">{plan.name}</h3>
                  <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className="text-xs font-normal text-zinc-400 mb-5 tracking-wide">{plan.description}</p>
                  <div className="flex items-end justify-center gap-1">
                    <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-4xl font-normal text-zinc-900">{plan.price}</span>
                    <span className="text-sm font-normal text-zinc-400 mb-1">{displayPeriod(plan.period)}</span>
                  </div>
                </div>
                <div className="w-12 h-px mx-auto mb-6" style={{ backgroundColor: gold, opacity: 0.3 }} />
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={cn('flex items-center gap-2.5 text-sm font-normal tracking-wide',
                      feature.included ? 'text-zinc-600' : 'text-zinc-300'
                    )}>
                      {feature.included
                        ? <Check className="w-4 h-4 shrink-0" style={{ color: gold }} />
                        : <X className="w-4 h-4 shrink-0 opacity-40" />
                      }
                      <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                  className={cn(
                    'block w-full text-center py-2.5 text-sm font-normal tracking-wider uppercase transition-colors border',
                    plan.highlighted
                      ? 'text-brand-ink hover:opacity-90'
                      : 'text-zinc-700 border-zinc-300 hover:border-zinc-400'
                  )}
                  style={plan.highlighted ? { backgroundColor: gold, borderColor: gold } : undefined}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ─────────────────────────────────────────────
  // CREATIVE (neobrutalist)
  // ─────────────────────────────────────────────
  if (universe === 'creative') {
    const accent = accentColor ?? '#6366f1'

    if (layout === 'comparison') {
      const features = allFeatureTexts(plans)
      return (
        <section className="relative bg-[#f5f0e8] overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
              {content.eyebrow ? (
                <span className="inline-block text-xs font-black uppercase px-3 py-1 bg-brand-cream text-brand-ink border-2 border-zinc-900">
                  {content.eyebrow}
                </span>
              ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
              {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-black text-zinc-900 uppercase", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
              {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-600 max-w-2xl mx-auto">{content.subtitle}</p>}
            </div>

            <div className="overflow-x-auto">
              <div className="border-2 border-zinc-900 shadow-[4px_4px_0_0_#18181b]">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-zinc-900">
                      <th className="text-left p-4 text-sm font-black uppercase text-zinc-900 w-1/4">Fonctionnalités</th>
                      {plans.map((plan, planIdx) => (
                        <th key={plan.id} className="p-4 text-center border-l-2 border-zinc-900">
                          <div className={cn('p-4', plan.highlighted ? 'bg-brand-cream text-white' : '')}>
                            {plan.badge && (
                              <span className={cn(
                                'inline-block text-[10px] font-black uppercase px-2 py-0.5 mb-2 border-2',
                                plan.highlighted ? 'border-white text-white' : 'border-zinc-900 text-zinc-900'
                              )}>
                                {plan.badge}
                              </span>
                            )}
                            <div {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-black text-lg">{plan.name}</div>
                            <div className="flex items-end justify-center gap-1 mt-1">
                              <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-3xl font-black">{plan.price}</span>
                              <span className="text-sm mb-1 opacity-60">{displayPeriod(plan.period)}</span>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feat, i) => (
                      <tr key={i} className="border-b border-zinc-300">
                        <td className="p-4 text-sm font-bold text-zinc-900">{feat}</td>
                        {plans.map((plan, planIdx) => (
                          <td key={plan.id} className="p-4 text-center border-l-2 border-zinc-900">
                            {planHasFeature(plan, feat)
                              ? <Check className="w-5 h-5 mx-auto text-zinc-900" />
                              : <X className="w-5 h-5 mx-auto text-zinc-300" />
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-zinc-900">
                      <td className="p-4" />
                      {plans.map((plan, planIdx) => (
                        <td key={plan.id} className="p-4 text-center border-l-2 border-zinc-900">
                          <a
                            {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                            className={cn(
                              'inline-block px-6 py-2.5 text-sm font-black uppercase transition-all border-2 border-zinc-900',
                              plan.highlighted
                                ? 'bg-brand-cream text-brand-ink hover:bg-zinc-800 shadow-[2px_2px_0_0_#18181b]'
                                : 'bg-white text-zinc-900 hover:bg-zinc-100 shadow-[2px_2px_0_0_#18181b]'
                            )}
                          >
                            {plan.cta}
                          </a>
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </section>
      )
    }

    // creative-columns
    return (
      <section className="relative bg-[#f5f0e8] overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
            {content.eyebrow ? (
              <span className="inline-block text-xs font-black uppercase px-3 py-1 bg-brand-cream text-brand-ink border-2 border-zinc-900">
                {content.eyebrow}
              </span>
            ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
            {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-black text-zinc-900 uppercase", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
            {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-600 max-w-2xl mx-auto">{content.subtitle}</p>}
          </div>

          <div className={cn(
            'grid gap-6 items-start',
            plans.length <= 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : plans.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'
          )}>
            {plans.map((plan, planIdx) => (
              <div
                key={plan.id}
                className={cn(
                  'p-6 relative border-2 border-zinc-900 transition-all',
                  plan.highlighted
                    ? 'bg-brand-cream text-brand-ink shadow-[6px_6px_0_0_#18181b]'
                    : 'bg-white shadow-[4px_4px_0_0_#18181b]'
                )}
              >
                {plan.badge && (
                  <span className={cn(
                    'absolute -top-3 left-4 text-xs font-black uppercase px-3 py-1 border-2 border-zinc-900',
                    plan.highlighted ? 'bg-amber-400 text-zinc-900' : 'bg-brand-cream text-white'
                  )}>
                    {plan.badge}
                  </span>
                )}
                <div className="mb-6">
                  <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-black text-lg mb-1 uppercase">{plan.name}</h3>
                  <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className={cn('text-xs mb-4', plan.highlighted ? 'text-brand-ink/60' : 'text-zinc-500')}>{plan.description}</p>
                  <div className="flex items-end gap-1">
                    <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-4xl font-black">{plan.price}</span>
                    <span className={cn('text-sm mb-1', plan.highlighted ? 'text-brand-ink/60' : 'text-zinc-400')}>{displayPeriod(plan.period)}</span>
                  </div>
                </div>
                <div className={cn('w-full h-0.5 mb-6', plan.highlighted ? 'bg-white/20' : 'bg-brand-cream')} />
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={cn('flex items-center gap-2 text-sm font-bold',
                      plan.highlighted
                        ? feature.included ? 'text-white' : 'text-brand-ink/30'
                        : feature.included ? 'text-zinc-900' : 'text-zinc-300'
                    )}>
                      {feature.included
                        ? <Check className="w-4 h-4 shrink-0" />
                        : <X className="w-4 h-4 shrink-0" />
                      }
                      <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                  className={cn(
                    'block w-full text-center py-2.5 text-sm font-black uppercase transition-all border-2 border-zinc-900',
                    plan.highlighted
                      ? 'bg-white text-zinc-900 hover:bg-zinc-100 shadow-[2px_2px_0_0_#f5f0e8]'
                      : 'bg-brand-cream text-brand-ink hover:bg-zinc-800 shadow-[2px_2px_0_0_#18181b]'
                  )}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ─────────────────────────────────────────────
  // ECOMMERCE
  // ─────────────────────────────────────────────
  if (universe === 'ecommerce') {
    const green = accentColor ?? '#059669'

    if (layout === 'comparison') {
      const features = allFeatureTexts(plans)
      return (
        <section className="relative bg-white overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
              {content.eyebrow ? (
                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-brand-ink" style={{ backgroundColor: green }}>
                  {content.eyebrow}
                </span>
              ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
              {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
              {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left p-4 text-sm font-medium text-zinc-500 w-1/4">Fonctionnalités</th>
                    {plans.map((plan, planIdx) => (
                      <th key={plan.id} className="p-4 text-center">
                        <div className={cn(
                          'rounded-xl p-5 border',
                          plan.highlighted ? 'border-2 bg-emerald-50' : 'border-zinc-200'
                        )}
                          style={plan.highlighted ? { borderColor: green } : undefined}
                        >
                          {plan.badge && (
                            <span className="inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-full text-brand-ink mb-2" style={{ backgroundColor: green }}>
                              {plan.badge || 'Meilleure offre'}
                            </span>
                          )}
                          <div {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-bold text-lg text-zinc-900">{plan.name}</div>
                          <div className="flex items-end justify-center gap-1 mt-1">
                            <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-3xl font-bold text-zinc-900">{plan.price}</span>
                            <span className="text-sm text-zinc-400 mb-1">{displayPeriod(plan.period)}</span>
                          </div>
                          {plan.highlighted && (
                            <p className="text-[11px] mt-1 font-semibold" style={{ color: green }}>Économisez jusqu&apos;à 30%</p>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map((feat, i) => (
                    <tr key={i} className={cn('border-b border-zinc-100', i % 2 === 0 ? 'bg-zinc-50/50' : '')}>
                      <td className="p-4 text-sm text-zinc-700">{feat}</td>
                      {plans.map((plan, planIdx) => (
                        <td key={plan.id} className="p-4 text-center">
                          {planHasFeature(plan, feat)
                            ? <Check className="w-5 h-5 mx-auto" style={{ color: green }} />
                            : <X className="w-5 h-5 mx-auto text-zinc-300" />
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="p-4" />
                    {plans.map((plan, planIdx) => (
                      <td key={plan.id} className="p-4 text-center">
                        <a
                          {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                          className={cn(
                            'inline-block px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors',
                            plan.highlighted
                              ? 'text-brand-ink hover:opacity-90'
                              : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                          )}
                          style={plan.highlighted ? { backgroundColor: green } : undefined}
                        >
                          {plan.cta}
                        </a>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Trust badges */}
            <div className="flex items-center justify-center gap-6 mt-10 text-xs text-zinc-400">
              <span className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" style={{ color: green }} /> Paiement sécurisé
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" style={{ color: green }} /> Satisfait ou remboursé
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" style={{ color: green }} /> Sans engagement
              </span>
            </div>
          </div>
        </section>
      )
    }

    // ecommerce-columns
    return (
      <section className="relative bg-white overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
            {content.eyebrow ? (
              <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-brand-ink" style={{ backgroundColor: green }}>
                {content.eyebrow}
              </span>
            ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
            {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
            {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
          </div>

          <div className={cn(
            'grid gap-6 items-start',
            plans.length <= 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : plans.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'
          )}>
            {plans.map((plan, planIdx) => (
              <div
                key={plan.id}
                className={cn(
                  'rounded-xl p-6 relative border',
                  plan.highlighted
                    ? 'border-2 bg-emerald-50'
                    : 'border-zinc-200 bg-white shadow-sm'
                )}
                style={plan.highlighted ? { borderColor: green } : undefined}
              >
                {plan.badge && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full text-brand-ink"
                    style={{ backgroundColor: green }}
                  >
                    {plan.badge}
                  </span>
                )}
                <div className="mb-6">
                  <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-bold text-lg mb-1 text-zinc-900">{plan.name}</h3>
                  <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className="text-xs mb-4 text-zinc-500">{plan.description}</p>
                  <div className="flex items-end gap-1">
                    <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-4xl font-bold text-zinc-900">{plan.price}</span>
                    <span className="text-sm mb-1 text-zinc-400">{displayPeriod(plan.period)}</span>
                  </div>
                  {plan.highlighted && (
                    <p className="text-xs mt-1 font-semibold" style={{ color: green }}>Économisez jusqu&apos;à 30%</p>
                  )}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={cn('flex items-center gap-2 text-sm',
                      feature.included ? 'text-zinc-700' : 'text-zinc-400'
                    )}>
                      {feature.included
                        ? <Check className="w-4 h-4 shrink-0" style={{ color: green }} />
                        : <X className="w-4 h-4 shrink-0 opacity-40" />
                      }
                      <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                  className={cn(
                    'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-colors',
                    plan.highlighted
                      ? 'text-brand-ink hover:opacity-90'
                      : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                  )}
                  style={plan.highlighted ? { backgroundColor: green } : undefined}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-10 text-xs text-zinc-400">
            <span className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" style={{ color: green }} /> Paiement sécurisé
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" style={{ color: green }} /> Satisfait ou remboursé
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" style={{ color: green }} /> Sans engagement
            </span>
          </div>
        </div>
      </section>
    )
  }

  // ─────────────────────────────────────────────
  // GLASS
  // ─────────────────────────────────────────────
  if (universe === 'glass') {
    const accent = accentColor ?? '#a78bfa'

    if (layout === 'comparison') {
      const features = allFeatureTexts(plans)
      return (
        <section className="relative bg-brand-cream overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          {/* Noise overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'none' }} />
          {/* Dot grid */}
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'none', backgroundSize: '24px 24px' }} />
          {/* Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full opacity-10 blur-3xl pointer-events-none" style={{ background: `radial-gradient(ellipse, ${accent}, transparent 70%)` }} />

          <div className="relative max-w-5xl mx-auto px-6 py-20">
            <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
              {content.eyebrow ? (
                <span className="inline-block text-xs font-medium px-3 py-1 rounded-full border border-white/10 text-brand-ink/60 backdrop-blur-sm bg-white/5">
                  {content.eyebrow}
                </span>
              ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
              {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-brand-ink", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
              {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-brand-ink/40 max-w-2xl mx-auto">{content.subtitle}</p>}
            </div>

            <div className="overflow-x-auto">
              <div className="rounded-2xl border border-white/10 backdrop-blur-md bg-white/[0.03] overflow-hidden">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-sm font-medium text-brand-ink/40 w-1/4">Fonctionnalités</th>
                      {plans.map((plan, planIdx) => (
                        <th key={plan.id} className="p-4 text-center">
                          <div className={cn(
                            'rounded-xl p-5 backdrop-blur-sm',
                            plan.highlighted
                              ? 'bg-white/[0.08] border border-white/20 shadow-lg'
                              : 'bg-white/[0.03] border border-white/5'
                          )}
                            style={plan.highlighted ? { boxShadow: `0 0 30px ${accent}15` } : undefined}
                          >
                            {plan.badge && (
                              <span className="inline-block text-[10px] font-medium px-2.5 py-0.5 rounded-full border border-white/20 text-brand-ink/70 mb-2 bg-white/5">
                                {plan.badge}
                              </span>
                            )}
                            <div {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-bold text-lg text-brand-ink">{plan.name}</div>
                            <div className="flex items-end justify-center gap-1 mt-1">
                              <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-3xl font-bold text-brand-ink">{plan.price}</span>
                              <span className="text-sm text-brand-ink/30 mb-1">{displayPeriod(plan.period)}</span>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feat, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="p-4 text-sm text-brand-ink/60">{feat}</td>
                        {plans.map((plan, planIdx) => (
                          <td key={plan.id} className="p-4 text-center">
                            {planHasFeature(plan, feat)
                              ? <Check className="w-5 h-5 mx-auto" style={{ color: accent }} />
                              : <X className="w-5 h-5 mx-auto text-brand-ink/15" />
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="p-4" />
                      {plans.map((plan, planIdx) => (
                        <td key={plan.id} className="p-4 text-center">
                          <a
                            {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                            className={cn(
                              'inline-block px-6 py-2.5 rounded-xl text-sm font-semibold transition-all',
                              plan.highlighted
                                ? 'text-brand-ink hover:opacity-90 shadow-lg'
                                : (isDark ? 'text-white border border-white/40 hover:bg-white hover:text-brand-ink' : 'text-brand-ink border border-brand-ink/30 hover:bg-brand-ink hover:text-brand-cream')
                            )}
                            style={plan.highlighted ? { background: `linear-gradient(135deg, ${accent}, ${accent}99)`, boxShadow: `0 0 20px ${accent}30` } : undefined}
                          >
                            {plan.cta}
                          </a>
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </section>
      )
    }

    // glass-columns
    // Ordre MOBILE des cartes : du moins cher au plus cher, offres sans prix
    // (groupe) reléguées à la fin. Desktop = ordre du contenu (inchangé).
    const priceVal = (p: PricingPlan) => {
      const m = (p.price || '').replace(/[\s  ]/g, '').match(/\d[\d.,]*/)
      return m ? parseFloat(m[0].replace(',', '.')) : Infinity
    }
    const mobileRank = plans.map((_, i) => i).sort((a, b) => priceVal(plans[a]) - priceVal(plans[b]))
    const mobileOrderOf = (idx: number) => mobileRank.indexOf(idx)
    return (
      <section className={cn("relative overflow-hidden", !isDark && "bg-brand-cream")} style={{ fontFamily: 'var(--font-body, inherit)', background: isDark ? 'radial-gradient(ellipse 66% 62% at 50% 44%, #0a2247, #04173a 62%, #021734 100%)' : undefined }}>
        {/* Noise overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'none' }} />
        {/* Dot grid */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'none', backgroundSize: '24px 24px' }} />
        {/* Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full opacity-10 blur-3xl pointer-events-none" style={{ background: `radial-gradient(ellipse, ${accent}, transparent 70%)` }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-20">
          <div className={cn("text-center mb-8 sm:mb-14", textAlign && getTextAlignClass(textAlign))}>
            {content.eyebrow ? (
              <>
                <p
                  style={{
                    fontFamily: 'var(--font-body, inherit)',
                    fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
                    letterSpacing: '0.3em',
                    textTransform: 'uppercase',
                    color: accent,
                    fontWeight: 600,
                    marginBottom: '22px',
                  }}
                >
                  {content.eyebrow}
                </p>
                <div style={{ width: 46, height: 1, backgroundColor: accent, margin: '0 auto 26px' }} aria-hidden />
              </>
            ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
            {content.title && (
              <GrandTitle
                dark={isDark}
                color={textColor ?? (isDark ? '#F7EFE1' : '#021734')}
                editProps={elementProps(config.id, 'title', 'heading')}
                className={titleSize ? getTitleSizeClass(titleSize) : undefined}
                style={{ marginBottom: '20px' }}
              >
                {content.title}
              </GrandTitle>
            )}
            {content.subtitle && (
              <p
                {...elementProps(config.id, 'subtitle', 'text')}
                style={{
                  fontSize: 'clamp(15px, 0.875rem + 0.5vw, 18px)',
                  fontWeight: 400,
                  lineHeight: '160%',
                  color: isDark ? 'rgba(255,255,255,0.74)' : 'rgba(22, 36, 58, 0.72)',
                  maxWidth: '640px',
                  margin: '0 auto',
                }}
              >
                {content.subtitle}
              </p>
            )}
          </div>

          {/* Mobile carousel scroll-snap, sm+ : grid */}
          <style dangerouslySetInnerHTML={{ __html: `
            .pricing-carousel::-webkit-scrollbar { display: none; }
            .pricing-carousel { -ms-overflow-style: none; scrollbar-width: none; }
            /* MOBILE uniquement : réordonne les cartes (--m-order) sans toucher au desktop */
            @media (max-width: 639px) { .gde-glass-card { order: var(--m-order, 0); } }
          ` }} />
          <div
            ref={carouselRef}
            data-lenis-prevent
            className={cn(
              'pricing-carousel',
              'flex sm:flex-wrap sm:justify-center overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none',
              'gap-4 sm:gap-6 items-stretch',
              '-mx-4 px-4 sm:mx-0 sm:px-0 pt-4 sm:pt-0 pb-4 sm:pb-0',
              'sm:max-w-6xl sm:mx-auto'
            )}
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {plans.map((plan, planIdx) => (
              <div
                key={plan.id}
                className={cn(
                  'gde-glass-card',
                  'snap-center shrink-0 sm:shrink-0 sm:grow-0 basis-[85%] sm:basis-[352px] sm:max-w-[368px] min-w-0',
                  'rounded-2xl p-4 sm:p-6 relative backdrop-blur-md transition-all duration-300 hover:scale-[1.03] flex flex-col',
                  plan.highlighted
                    ? 'border shadow-2xl'
                    : (isDark ? 'bg-white/[0.06] border border-white/15 hover:bg-white/[0.1]' : 'bg-white/70 border border-brand-ink/12 shadow-sm hover:bg-white/90')
                )}
                style={{
                  ['--m-order' as string]: mobileOrderOf(planIdx),
                  ...(plan.highlighted
                    ? { borderColor: `${accent}60`, backgroundColor: `${accent}0a`, boxShadow: `0 0 40px ${accent}20, 0 20px 50px -12px rgba(0,0,0,0.5)` }
                    : {}),
                } as CSSProperties}
              >
                {/* Gradient border effect for highlighted */}
                {plan.highlighted && (
                  <div
                    className="absolute -inset-[1px] rounded-2xl -z-10 opacity-50"
                    style={{ background: `linear-gradient(135deg, ${accent}60, transparent 50%, ${accent}30)` }}
                  />
                )}
                {plan.badge && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-medium px-3 py-1 rounded-full"
                    style={plan.highlighted
                      ? { backgroundColor: accent, color: '#F7EFE1', border: 'none', fontWeight: 700 }
                      : { border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', backgroundColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }
                    }
                  >
                    {plan.badge}
                  </span>
                )}
                <div className="mb-4 sm:mb-6">
                  <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className="font-normal text-xl sm:text-2xl md:text-2xl mb-1 text-brand-ink" style={{ fontFamily: 'var(--font-heading, inherit)', color: isDark ? '#ffffff' : undefined }}>{plan.name}</h3>
                  <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className="text-[11px] sm:text-xs mb-3 sm:mb-4 text-brand-ink/40" style={{ color: isDark ? 'rgba(255,255,255,0.55)' : undefined }}>{plan.description}</p>
                  {!plan.masquerPrix && !modeReservation && (
                    <div className="flex items-end gap-2 flex-wrap">
                      <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className="text-2xl sm:text-3xl md:text-4xl font-bold text-brand-ink" style={{ color: isDark ? '#ffffff' : undefined }}>{plan.price}</span>
                      {(plan as any).originalPrice && (
                        <span className="text-sm sm:text-base md:text-lg line-through mb-1 text-brand-ink/30">{(plan as any).originalPrice}</span>
                      )}
                      <span className="text-xs sm:text-sm mb-1 text-brand-ink/30" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : undefined }}>{displayPeriod(plan.period)}</span>
                    </div>
                  )}
                  {/* Chaque formule ouvre à une heure différente : champ éditable
                      au CRM (`plans[].horaires`), jamais affiché jusqu'ici. Une
                      formule sans horaires renseignés n'affiche rien de plus,
                      rendu inchangé. */}
                  {plan.horaires && (
                    <p {...elementProps(config.id, `plans.${planIdx}.horaires`, 'text')} className="text-[11px] sm:text-xs mt-1.5 text-brand-ink/40" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : undefined }}>
                      Accès {plan.horaires}
                    </p>
                  )}
                </div>
                <div className="w-full h-px bg-white/10 mb-4 sm:mb-6" />
                <ul className="space-y-1.5 sm:space-y-2.5 mb-4 sm:mb-6 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={cn('flex items-center gap-2 text-[13px] sm:text-sm',
                      feature.included ? (isDark ? 'text-white/80' : 'text-brand-ink/80') : (isDark ? 'text-white/25' : 'text-brand-ink/20')
                    )}>
                      {feature.included
                        ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: accent }} />
                        : <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 opacity-40" />
                      }
                      <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                {modeReservation ? (
                  <button
                    {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                    type="button"
                    onClick={() => openReservation(plan.name)}
                    className={cn(
                      'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-all mt-auto cursor-pointer',
                      plan.highlighted
                        ? 'text-brand-ink hover:opacity-90 shadow-lg'
                        : (isDark ? 'text-white border border-white/40 hover:bg-white hover:text-brand-ink' : 'text-brand-ink border border-brand-ink/30 hover:bg-brand-ink hover:text-brand-cream')
                    )}
                    style={plan.highlighted ? { background: `linear-gradient(135deg, ${accent}, ${accent}99)`, boxShadow: `0 0 20px ${accent}30` } : undefined}
                  >
                    {/* 🔤 01/10 — LE LIBELLÉ ÉCRIT DANS LE CRM FAIT FOI.
                        Il était écrit EN DUR ici : le champ « Bouton — texte »
                        de l'éditeur existait, se saisissait, s'enregistrait et
                        se publiait… sans jamais rien changer à l'écran. Une
                        case qui ne sert à rien est une promesse non tenue
                        (signalé par Anastasia : « SOLD OUT », rien ne se
                        passe sur le site).
                        « Réserver ma place » reste le texte par défaut quand
                        le champ est vide — aucune formule déjà en ligne ne
                        change d'aspect.
                        ⚠️ Ce bouton OUVRE TOUJOURS le formulaire, quel que
                        soit son texte : rendre une formule réellement
                        indisponible demande la case « Formule complète »
                        (chiffrée à part, décision de Jeason). */}
                    {plan.cta || 'Réserver ma place'}
                  </button>
                ) : embedUrl && !plan.boutonContact ? (
                  <button
                    {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                    onClick={() => setModalOpen(true)}
                    className={cn(
                      'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-all mt-auto cursor-pointer',
                      plan.highlighted
                        ? 'text-brand-ink hover:opacity-90 shadow-lg'
                        : (isDark ? 'text-white border border-white/40 hover:bg-white hover:text-brand-ink' : 'text-brand-ink border border-brand-ink/30 hover:bg-brand-ink hover:text-brand-cream')
                    )}
                    style={plan.highlighted ? { background: `linear-gradient(135deg, ${accent}, ${accent}99)`, boxShadow: `0 0 20px ${accent}30` } : undefined}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <a
                    {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                    href={plan.ctaHref}
                    className={cn(
                      'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-all mt-auto',
                      plan.highlighted
                        ? 'text-brand-ink hover:opacity-90 shadow-lg'
                        : (isDark ? 'text-white border border-white/40 hover:bg-white hover:text-brand-ink' : 'text-brand-ink border border-brand-ink/30 hover:bg-brand-ink hover:text-brand-cream')
                    )}
                    style={plan.highlighted ? { background: `linear-gradient(135deg, ${accent}, ${accent}99)`, boxShadow: `0 0 20px ${accent}30` } : undefined}
                  >
                    {plan.cta}
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Mobile dots — masqués sur sm+ */}
          {plans.length > 1 && (
            <div className="flex justify-center gap-2 mt-6 sm:hidden">
              {plans.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => scrollToCard(idx)}
                  aria-label={`Aller à la formule ${idx + 1}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: idx === currentMobileIdx ? 24 : 6,
                    backgroundColor: idx === currentMobileIdx ? accent : 'rgba(255,255,255,0.2)',
                  }}
                />
              ))}
            </div>
          )}

          {/* la billetterie modal */}
          {modalOpen && embedUrl && (
            <BilletterieModal embedUrl={embedUrl} accentColor={accent} variant="dark" footerText={footerText} onClose={closeModal} />
          )}

          {/* Modale de demande de réservation (mode `modeReservation`) */}
          {modeReservation && reservationOpen && (
            <ReservationModal
              open={reservationOpen}
              onClose={closeReservation}
              config={reservationConfig}
              formules={nomsFormules}
              formuleParDefaut={reservationFormule}
            />
          )}
        </div>
      </section>
    )
  }

  // ─────────────────────────────────────────────
  // FALLBACK (startup-columns)
  // ─────────────────────────────────────────────
  const accent = accentColor ?? '#6366f1'
  return (
    <section className="relative bg-zinc-50 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
          {content.eyebrow ? (
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-brand-ink" style={{ backgroundColor: accent }}>
              {content.eyebrow}
            </span>
          ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
          {content.title && <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl sm:text-4xl font-bold text-zinc-900", titleSize && getTitleSizeClass(titleSize))} style={textColor ? { color: textColor } : undefined}>{content.title}</h2>}
          {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
        </div>
        <div className={cn(
          'grid gap-6 items-start',
          plans.length <= 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : 'sm:grid-cols-3'
        )}>
          {plans.map((plan, planIdx) => (
            <div
              key={plan.id}
              className={cn(
                'rounded-2xl p-6 relative transition-all',
                plan.highlighted
                  ? 'text-brand-ink scale-105 shadow-2xl ring-2'
                  : 'bg-white shadow-sm border border-zinc-100'
              )}
              style={plan.highlighted
                ? { backgroundColor: accent, ringColor: accent, '--tw-ring-color': accent, boxShadow: `0 20px 50px -12px ${accent}40` } as React.CSSProperties
                : undefined
              }
            >
              {plan.badge && (
                <span className={cn(
                  'absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full',
                  plan.highlighted ? 'bg-amber-400 text-amber-900' : 'bg-zinc-800 text-white'
                )}>
                  {plan.badge}
                </span>
              )}
              <div className="mb-6">
                <h3 {...elementProps(config.id, `plans.${planIdx}.name`, 'heading')} className={cn('font-bold text-lg mb-1', plan.highlighted ? 'text-white' : 'text-zinc-900')}>{plan.name}</h3>
                <p {...elementProps(config.id, `plans.${planIdx}.description`, 'text')} className={cn('text-xs mb-4', plan.highlighted ? 'text-brand-ink/70' : 'text-zinc-500')}>{plan.description}</p>
                <div className="flex items-end gap-1">
                  <span {...elementProps(config.id, `plans.${planIdx}.price`, 'text')} className={cn('text-4xl font-bold', plan.highlighted ? 'text-white' : 'text-zinc-900')}>{plan.price}</span>
                  <span className={cn('text-sm mb-1', plan.highlighted ? 'text-brand-ink/70' : 'text-zinc-400')}>{displayPeriod(plan.period)}</span>
                </div>
              </div>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className={cn('flex items-center gap-2 text-sm',
                    plan.highlighted ? 'text-white' : feature.included ? 'text-zinc-700' : 'text-zinc-400'
                  )}>
                    {feature.included
                      ? <Check className={cn('w-4 h-4 shrink-0', plan.highlighted ? 'text-brand-ink/80' : '')} style={!plan.highlighted ? { color: accent } : undefined} />
                      : <X className="w-4 h-4 shrink-0 opacity-40" />
                    }
                    <span {...elementProps(config.id, `plans.${planIdx}.features.${i}`, 'text')}>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <a
                {...elementProps(config.id, `plans.${planIdx}.cta`, 'button')}
                          href={plan.ctaHref}
                className={cn(
                  'block w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-colors',
                  plan.highlighted
                    ? 'bg-white hover:bg-white/90'
                    : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-200'
                )}
                style={plan.highlighted ? { color: accent } : undefined}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export const pricingMeta = {
  type: 'pricing',
  label: 'Tarifs',
  icon: '💰',
  variants: [
    'startup-columns', 'startup-comparison',
    'corporate-columns', 'corporate-comparison',
    'luxe-columns', 'luxe-comparison',
    'creative-columns', 'creative-comparison',
    'ecommerce-columns', 'ecommerce-comparison',
    'glass-columns', 'glass-comparison',
  ],
  defaultVariant: 'startup-columns',
  defaultContent: {},
}
